import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { CellularBillingRow, CellularPoolingSnapshot, CellularPoolSummary } from '../../../../shared/model/cellular-billing';
import { calculatePooling } from './cellular-pooling-calculation';

type Mode = 'ORGANIZATION' | 'FULL_MSP';
interface MspGroup {
  id: string; msp: string; mode: Mode; pools: CellularPoolSummary[];
  eligibleDevices: number; allowanceGb: number; usageGb: number; remainingGb: number;
  chargeGb: number; blocked: number; ready: number;
}
@Component({ selector: 'app-cellular-pooling', templateUrl: './cellular-pooling.component.html', styleUrls: ['./cellular-pooling.component.scss'] })
export class CellularPoolingComponent implements OnChanges {
  @Input() rows: CellularBillingRow[] = [];
  @Input() snapshot: CellularPoolingSnapshot;
  @Input() available = true;
  @Input() readOnly = false;
  @Input() revision = 0;
  @Output() policiesChange = new EventEmitter<{ msp: string; mode: Mode }[]>();
  groups: MspGroup[] = [];
  visibleGroups: MspGroup[] = [];
  expanded = new Set<string>();
  expandedPools = new Set<string>();
  memberLimits = new Map<string, number>();
  search = '';
  organizationSearch = '';
  modeFilter = '';
  sortKey = 'msp';
  ascending = true;
  exceptionsOnly = false;
  showZeroUsage = false;
  totalChargeGb = 0;
  eligibleDevices = 0;
  allocatedGb = 0;
  usageGb = 0;
  remainingGb = 0;
  blockedPools = 0;
  calculationError = '';
  changedModes = new Set<string>();
  modeNotice = '';
  private acceptedSnapshot: CellularPoolingSnapshot;
  get historical(): boolean { return !!this.snapshot && this.snapshot.version < 3; }

  ngOnChanges(): void {
    if (this.acceptedSnapshot !== this.snapshot) {
      this.acceptedSnapshot = this.snapshot;
      this.changedModes.clear();
    }
    this.calculationError = '';
    try {
      // The display hierarchy always follows License organization mappings.
      // Historical billing totals remain the saved MSP totals, not these groups.
      const pools: CellularPoolSummary[] = calculatePooling(this.rows || [], !this.historical && this.snapshot && this.snapshot.policies || []).pools;
      const grouped = new Map<string, MspGroup>();
      pools.forEach(pool => {
        const id = pool.msp.trim().toLowerCase();
        if (!grouped.has(id)) grouped.set(id, { id, msp: pool.msp, mode: pool.mode || 'ORGANIZATION', pools: [], eligibleDevices: 0, allowanceGb: 0, usageGb: 0, remainingGb: 0, chargeGb: 0, blocked: 0, ready: 0 });
        const group = grouped.get(id);
        group.pools.push(pool);
        group.eligibleDevices += pool.eligibleDevices;
        group.usageGb += pool.usageGb;
        if (pool.status === 'READY') {
          group.allowanceGb += pool.allowanceGb; group.remainingGb += pool.remainingGb;
          group.chargeGb += pool.chargeGb || 0; group.ready++;
        }
        if (pool.status === 'BLOCKED') group.blocked++;
      });
      if (this.historical) grouped.forEach(group => {
        const saved = (this.snapshot.pools || []).filter(pool => pool.msp.trim().toLowerCase() === group.msp.trim().toLowerCase());
        const ready = saved.filter(pool => pool.status === 'READY');
        group.allowanceGb = ready.reduce((sum, pool) => sum + pool.allowanceGb, 0);
        group.remainingGb = ready.reduce((sum, pool) => sum + pool.remainingGb, 0);
        group.chargeGb = ready.reduce((sum, pool) => sum + (pool.chargeGb || 0), 0);
        group.ready = ready.length;
        group.blocked = saved.filter(pool => pool.status === 'BLOCKED').length;
      });
      this.groups = Array.from(grouped.values()).sort((a, b) => a.msp.localeCompare(b.msp));
      this.totalChargeGb = this.groups.reduce((sum, group) => sum + group.chargeGb, 0);
      this.eligibleDevices = this.groups.reduce((sum, group) => sum + group.eligibleDevices, 0);
      this.allocatedGb = this.groups.reduce((sum, group) => sum + group.allowanceGb, 0);
      this.usageGb = this.groups.reduce((sum, group) => sum + group.usageGb, 0);
      this.remainingGb = this.groups.reduce((sum, group) => sum + group.remainingGb, 0);
      this.blockedPools = this.groups.reduce((sum, group) => sum + group.blocked, 0);
      this.applyFilters();
    } catch (_) {
      this.groups = []; this.visibleGroups = [];
      this.calculationError = 'Unable to calculate pooling. Reload the report and check its pooling configuration.';
    }
  }
  applyFilters(): void {
    const search = this.search.trim().toLowerCase(), org = this.organizationSearch.trim().toLowerCase();
    this.visibleGroups = this.groups.filter(group => (!this.modeFilter || group.mode === this.modeFilter)
      && (!this.exceptionsOnly || !!group.blocked)
      && (!search || group.msp.toLowerCase().includes(search) || group.pools.some(pool => pool.members.some(member => (member.mac + ' ' + member.deviceName).toLowerCase().includes(search)))))
      .map(group => ({ ...group, pools: group.pools.filter(pool => (!org || (pool.organization || '').toLowerCase().includes(org) || (group.mode === 'FULL_MSP' && pool.members.some(member => (member.organization || '').toLowerCase().includes(org)))) && (!this.exceptionsOnly || pool.status === 'BLOCKED')) }))
      .filter(group => group.pools.length > 0)
      .sort((a, b) => {
        const left = a[this.sortKey], right = b[this.sortKey];
        const comparison = typeof left === 'number' ? left - right : String(left).localeCompare(String(right));
        return this.ascending ? comparison : -comparison;
      });
    if (org || search || this.exceptionsOnly) this.visibleGroups.forEach(group => this.expanded.add(group.id));
  }
  clearFilters(): void { this.search = ''; this.organizationSearch = ''; this.modeFilter = ''; this.exceptionsOnly = false; this.applyFilters(); }
  sort(key: string): void { this.ascending = this.sortKey === key ? !this.ascending : true; this.sortKey = key; this.applyFilters(); }
  sortState(key: string): string { return this.sortKey === key ? this.ascending ? 'ascending' : 'descending' : 'none'; }
  toggle(group: MspGroup): void { this.expanded.has(group.id) ? this.expanded.delete(group.id) : this.expanded.add(group.id); }
  togglePool(pool: CellularPoolSummary): void { this.expandedPools.has(pool.id) ? this.expandedPools.delete(pool.id) : this.expandedPools.add(pool.id); }
  collapseAll(): void { this.expanded.clear(); this.expandedPools.clear(); }
  ledgerMembers(pool: CellularPoolSummary) {
    const search = this.search.trim().toLowerCase();
    // Filter the ledger view only; precomputed balances always use the full pool.
    const ordered = pool.members.map((member, index) => ({ ...member, allocationOrder: index + 1 }));
    const members = search && !pool.msp.toLowerCase().includes(search)
      ? ordered.filter(member => (member.mac + ' ' + member.deviceName).toLowerCase().includes(search)) : ordered;
    const visible = members.filter(member => this.showZeroUsage || member.verificationGb > 0)
      .slice().sort((a, b) => b.verificationGb - a.verificationGb || a.mac.localeCompare(b.mac));
    return { total: visible.length, eligible: pool.eligibleDevices, hiddenZero: members.filter(member => member.verificationGb === 0).length,
      rows: visible.slice(0, this.memberLimits.get(pool.id) || 100) };
  }
  showMore(pool: CellularPoolSummary): void { this.memberLimits.set(pool.id, (this.memberLimits.get(pool.id) || 100) + 100); }
  requestMode(group: MspGroup, mode: Mode): void {
    if (this.readOnly || (!this.historical && group.mode === mode)) return;
    if (this.historical && !window.confirm('Apply the new pooling rules to this report? The original remains unchanged. Save Revision to retain the recalculated analysis.')) return;
    const policies = (this.snapshot && this.snapshot.policies || []).filter(policy => policy.msp.trim().toLowerCase() !== group.msp.trim().toLowerCase());
    // Persist Org explicitly as well, so future defaults cannot override it.
    policies.push({ msp: group.msp, mode });
    this.changedModes.add(group.id);
    this.modeNotice = group.msp + ': ' + (mode === 'FULL_MSP' ? 'Full MSP Pooling' : 'Organization Pooling') + '. Unsaved; save the report revision to retain this change.';
    this.policiesChange.emit(policies);
  }
  trackGroup(index: number, group: MspGroup): string { return group.id; }
  trackPool(index: number, pool: CellularPoolSummary): string { return pool.id; }
  trackMember(index: number, member: { rowId: string }): string { return member.rowId; }
}
